/** The single host for app notices: a centered modal, portaled to <body>, that
 *  renders whatever the noticeStore currently holds. Mount once near the app
 *  root. Dismiss on OK, Enter, Escape, or backdrop click. */
import React from "react";
import { createPortal } from "react-dom";
import { subscribeNotice, dismissNotice, type AppNotice } from "./noticeStore";
import "./Notice.css";

const Notice: React.FC = () => {
  const [notice, setNotice] = React.useState<AppNotice | null>(null);

  React.useEffect(() => subscribeNotice(setNotice), []);

  React.useEffect(() => {
    if (!notice) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissNotice(false);
      else if (e.key === "Enter") dismissNotice(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [notice]);

  if (!notice) return null;

  return createPortal(
    <div className="notice-backdrop" onMouseDown={() => dismissNotice(false)}>
      <div
        className={`notice-modal${notice.tone === "error" ? " notice-modal--error" : ""}`}
        role="alertdialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="notice-message">{notice.message}</div>
        <div className="notice-actions">
          {notice.confirm && (
            <button
              type="button"
              className="notice-cancel"
              onClick={() => dismissNotice(false)}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            className="notice-ok"
            onClick={() => dismissNotice(true)}
            autoFocus
          >
            {notice.confirm ? "Confirm" : "OK"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Notice;
